import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { BookOpen, User, Calendar, Edit, Trash2, Sparkles } from 'lucide-react';

interface Case {
  id: string;
  case_number?: string;
  title: string;
  description?: string;
  case_type: string;
  status: string;
  priority: string;
  deadline?: string;
  lawyer_responsible?: string;
  created_at: string;
  client_id?: string;
  is_litisconsorcio?: boolean;
  clients?: {
    name: string;
    email: string;
  } | {
    name: string;
    email: string;
  }[];
}

interface CaseCardProps {
  case_: Case;
  onEdit: (case_: Case) => void;
  onDelete: (id: string) => void;
  onAnalyze: (case_: Case) => void;
  onClick?: (case_: Case) => void;
}

export const CaseCard = ({ case_, onEdit, onDelete, onAnalyze, onClick }: CaseCardProps) => {
  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case "Alta": return "bg-destructive text-destructive-foreground";
      case "Média": return "bg-warning text-warning-foreground";
      case "Baixa": return "bg-secondary text-secondary-foreground";
      default: return "bg-muted text-muted-foreground";
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "Concluído": return "bg-tertiary text-tertiary-foreground";
      case "Em andamento": return "bg-primary text-primary-foreground";
      case "Aguardando documentos": return "bg-warning text-warning-foreground";
      case "Suspenso": return "bg-muted text-muted-foreground";
      default: return "bg-muted text-muted-foreground";
    }
  };

  return (
    <Card 
      className="bg-white hover:shadow-lg transition-shadow cursor-pointer border border-[#c4ab83]/30 hover:border-[#c4ab83]/50"
      onClick={() => onClick?.(case_)}
    >
      <CardHeader className="pb-3">
        <div className="flex justify-between items-start">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
              <BookOpen className="w-5 h-5 text-primary" />
            </div>
            <div className="flex-1">
              <CardTitle className="text-lg line-clamp-1">{case_.title}</CardTitle>
              {case_.case_number && (
                <p className="text-sm text-muted-foreground">{case_.case_number}</p>
              )}
            </div>
          </div>
          <div className="flex gap-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onAnalyze(case_)}
              title="Análise com IA"
            >
              <Sparkles className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onEdit(case_)}
            >
              <Edit className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onDelete(case_.id)}
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex gap-2">
          <Badge className={getStatusColor(case_.status)}>
            {case_.status}
          </Badge>
          <Badge className={getPriorityColor(case_.priority)}>
            {case_.priority}
          </Badge>
        </div>
        
        <Badge variant="outline">{case_.case_type}</Badge>
        
        {case_.clients && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <User className="w-4 h-4" />
            {case_.is_litisconsorcio ? (
              <span>
                {Array.isArray(case_.clients) ? case_.clients.map(client => client.name).join(', ') : case_.clients.name || 'Cliente não informado'}
                <Badge variant="outline" className="ml-2 text-xs">
                  Litisconsórcio
                </Badge>
              </span>
            ) : (
              Array.isArray(case_.clients) ? case_.clients[0]?.name : case_.clients.name || 'Cliente não informado'
            )}
          </div>
        )}
        
        {case_.deadline && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Calendar className="w-4 h-4" />
            Prazo: {new Date(case_.deadline).toLocaleDateString('pt-BR')}
          </div>
        )}
        
        {case_.lawyer_responsible && (
          <p className="text-sm text-muted-foreground">
            Responsável: {case_.lawyer_responsible}
          </p>
        )}
      </CardContent>
    </Card>
  );
};